import { useGetMerchantQuery } from "Api/Queries/Merchants/useGetMerchantQuery";
import { useUploadMerchantAvatarMutation } from "Api/Mutations/Merchants/useUploadMerchantAvatarMutation";
import { useDeleteMerchantAvatarMutation } from "Api/Mutations/Merchants/useDeleteMerchantAvatarMutation";
import { FormSection } from "Components/Shared/FormSection";
import { ProfilePhotoEditor } from "Components/Shared/ProfilePhoto/ProfilePhotoEditor";
import { Resources, useResource } from "Translations/Resources";
import { getContextProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";
import { toast } from "sonner";

const AVATAR_RESOURCES = Resources.Components.Merchant.Avatar;

/**
 * - editable: picker + remove.
 * - readOnly: display only, no explanation (the whole page is read-only for this viewer).
 * - lockedAfterOnboarding: display only, with the reason — hunters may change the avatar only
 *   while the merchant is onboarding (ZS-1535 rule, mirrored by MerchantLogoAuthorization).
 */
export type MerchantAvatarAccess =
  | "editable"
  | "readOnly"
  | "lockedAfterOnboarding";

type Props = {
  merchantPublicID?: string;
  access?: MerchantAvatarAccess;
};

const HINT_BY_ACCESS: Record<MerchantAvatarAccess, string | undefined> = {
  editable: AVATAR_RESOURCES.Hint,
  readOnly: undefined,
  lockedAfterOnboarding: AVATAR_RESOURCES.LockedAfterOnboardingHint,
};

/**
 * The merchant's image is the founder Merchant persona's avatar (ZS-1607). This is the
 * back-office uploader for callers who are not that persona, usable once the company has a
 * Merchant grant (for hunter-created merchants: after access is sent); the merchant itself
 * edits the same picture through the profile photo section of its account. Same editor and
 * crop flow as that section — only the subject and the mutations differ. Without a picture the
 * editor shows the company's initials, exactly as every other avatar in the app renders an
 * empty picture (no placeholder image).
 */
export const MerchantAvatarUpload = ({
  merchantPublicID,
  access = "editable",
}: Props) => {
  const { t } = useResource();
  const { data: merchantData } = useGetMerchantQuery(merchantPublicID);
  const { mutateAsync: uploadAvatar, isPending: isUploading } =
    useUploadMerchantAvatarMutation({
      onSuccess: () => toast.success(t(AVATAR_RESOURCES.UploadSuccess)),
      onError: () => toast.error(t(AVATAR_RESOURCES.UploadError)),
    });
  const { mutate: deleteAvatar, isPending: isDeleting } =
    useDeleteMerchantAvatarMutation({
      onSuccess: () => toast.success(t(AVATAR_RESOURCES.DeleteSuccess)),
      onError: () => toast.error(t(AVATAR_RESOURCES.DeleteError)),
    });

  if (!merchantData) {
    return null;
  }

  const {
    publicID,
    merchantIdentityUserPublicID,
    merchantProfilePicture,
    party: { companyName, firstName, lastName },
  } = merchantData;
  const avatarUrl = getContextProfilePhotoUrl(
    merchantIdentityUserPublicID,
    merchantProfilePicture,
  );
  // Company name for a legal entity, the person's initials for a sole trader — the same
  // name the merchant persona carries in chat.
  const acronym = (
    companyName
      ? companyName[0]
      : firstName && lastName
        ? `${firstName[0]}${lastName[0]}`
        : ((firstName ?? lastName ?? "")[0] ?? "")
  ).toLocaleUpperCase();
  const hintKey = HINT_BY_ACCESS[access];

  return (
    <FormSection legend={t(AVATAR_RESOURCES.Title)}>
      <ProfilePhotoEditor
        previewUrl={avatarUrl}
        fallback={acronym}
        hint={hintKey ? t(hintKey) : undefined}
        removeConfirmMessage={t(AVATAR_RESOURCES.RemoveConfirm)}
        isUploading={isUploading}
        isDeleting={isDeleting}
        onUpload={(photo) =>
          uploadAvatar({ merchantPublicID: publicID, file: photo })
        }
        onDelete={() => deleteAvatar({ merchantPublicID: publicID })}
        isReadOnly={access !== "editable"}
      />
    </FormSection>
  );
};
